import React, { useState, useEffect } from "react";
import { useDisclosure } from "@mantine/hooks";
import {
  Group,
  Button,
  Card,
  Image,
  Loader,
  TextInput,
  ActionIcon,
  Flex,
} from "@mantine/core";
import { ReactComponent as PlusIcon } from "@/assets/icons/plus.svg";
import { ReactComponent as XIcon } from "@/assets/icons/x.svg";
import { ReactComponent as SearchIcon } from "@/assets/icons/search.svg";
import AddOrEditModal from "@/components/Admin/Shared/AddOrEditModal/AddOrEditModal";
import { PublicationApi } from "@/api/publication.api";
import { Publication } from "@/api/types";
import "./Publications.css";

const Publications: React.FC = () => {
  const [opened, { open, close }] = useDisclosure(false);
  const [publications, setPublications] = useState<Publication[]>();
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [publicationToEdit, setPublicationToEdit] = useState<Publication>();

  const fetchPublications = async () => {
    setLoading(true);
    const publications = await PublicationApi.getPublications();
    if (publications) setPublications(publications);
    setLoading(false);
  };

  const filteredPublications = publications?.filter((publication) =>
    publication.title.toLowerCase().includes(searchValue.toLowerCase().trim())
  );

  useEffect(() => {
    fetchPublications();
  }, []);

  const closeModal = () => {
    fetchPublications();
    close();
    setPublicationToEdit(undefined);
  };

  const openEditModal = (publication: Publication) => {
    setPublicationToEdit(publication);
    open();
  };

  return (
    <div className="px-[5%]">
      <Group
        className="py-[1.5rem] flex min-h-[6rem] !items-center"
        justify="space-between"
      >
        <div className="text-[#0b1a29] text-2xl font-bold">Publikacije</div>
        <Group>
          <TextInput
            value={searchValue}
            placeholder="Pretraži publikacije"
            leftSection={<SearchIcon />}
            className="search-input"
            rightSection={
              searchValue && (
                <ActionIcon
                  variant="subtle"
                  aria-label="clear"
                  size="sm"
                  radius="xl"
                  onClick={() => setSearchValue("")}
                >
                  <XIcon width={10} />
                </ActionIcon>
              )
            }
            onChange={(event) => {
              setSearchValue(event.currentTarget.value);
            }}
          />
          <Button
            variant="filled"
            color="#004a99"
            radius="xl"
            leftSection={<PlusIcon />}
            size="md"
            className="!h-[3rem]"
            onClick={open}
          >
            Dodaj publikaciju
          </Button>
        </Group>
      </Group>
      {filteredPublications?.length ? (
        <Flex gap={50} wrap="wrap" className="publication-container">
          {filteredPublications.map((publication) => {
            return (
              <Card
                key={publication.id}
                className="publication-card"
                onClick={() => openEditModal(publication)}
              >
                <Card.Section inheritPadding className="pt-4 pb-2">
                  <Image
                    src={publication.cover.url}
                    className="!bg-cover !rounded publication-image"
                  />
                </Card.Section>
                <Card.Section
                  inheritPadding
                  className="pb-4 !flex !flex-col gap-1"
                >
                  <div className="text-[#0b1a29] text-base font-bold text-ellipsis">
                    {publication.title}
                  </div>
                  <div className="text-[#515151] text-sm font-medium list-item-desc text-ellipsis">
                    {publication.author}
                  </div>
                  <div className="text-[#004a99] text-sm font-bold">
                    {publication.price} €
                  </div>
                </Card.Section>
              </Card>
            );
          })}
        </Flex>
      ) : loading ? (
        <div className="flex items-center justify-center mt-[5rem]">
          <Loader color="blue" />
        </div>
      ) : (
        <div className="flex mt-4">Trenutno nema publikacija.</div>
      )}
      <AddOrEditModal
        type="publication"
        initialData={publicationToEdit}
        opened={opened}
        onClose={closeModal}
      />
    </div>
  );
};

export default Publications;
